import { useGetPsiContractingProfile } from '@/lib/api/psi-profile';
import { styled, Container, Box, Alert, Button, Collapse } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import Header from '@/pages/layout/header/Header.tsx';
import Sidebar from '@/pages/layout/sidebar/Sidebar.tsx';
import { useAuth } from '@/hooks/useAuth.ts';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useGetMe } from '@/lib/api/me.ts';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import OnboardingInvestorModal from '@/features/investor/onboarding/OnboardingInvestorModal.tsx';
import OnboardingPsiModal from '@/features/psi/OnboardingPsiModal.tsx';
import { enqueueSnackbar } from 'notistack';
import { StackedSnackbar } from '@/components/ui/StackedSnackbar.tsx';
import WhatsAppFab from '@/components/ui/WhatsAppFab.tsx';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';

const MainWrapper = styled('div')(() => ({
  display: 'flex',
  minHeight: '100vh',
  width: '100%',
}));

const PageWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  paddingBottom: '60px',
  paddingTop: '70px',
  flexDirection: 'column',
  zIndex: 1,
  backgroundColor: 'transparent',
  marginLeft: 0,
  [theme.breakpoints.down('lg')]: {
    paddingTop: '70px',
  },
}));

export default function DashboardLayout() {
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useGetMe();
  const { data: contracting } = useGetPsiContractingProfile(
    user?.role === 'psi' && user.onboardingCompleted
  );
  const { identifyUser, trackEvent } = useAnalytics();
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => sessionStorage.getItem('onboarding_dismissed') === 'true'
  );
  const hasIdentifiedUser = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user && !hasIdentifiedUser.current) {
      identifyUser(user.id, {
        role: user.role,
        name: user.name,
        email: user.email,
        city: user.city,
        province: user.province,
        onboardingCompleted: user.onboardingCompleted,
        profileCompleted: user.profileCompleted,
        suitabilityTestStatus: user.suitabilityTestStatus,
        createdAt: user.createdAt,
      });
      hasIdentifiedUser.current = true;
    }
  }, [user, identifyUser]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const profileIncompleteBannerOpen =
    user?.role === 'psi' &&
    contracting &&
    !contracting.readyToContract &&
    location.pathname !== '/perfil';

  const showOnboarding = user && !user.onboardingCompleted && !onboardingDismissed;

  if (!isAuthenticated) return null;

  const handleCompleteOnboarding = () => {
    if (user) {
      trackProductEvent(trackEvent, 'onboarding_completed', {
        role: user.role,
        platform: ANALYTICS_PLATFORM,
      });
    }

    setOnboardingDismissed(true);
    enqueueSnackbar(
      <StackedSnackbar
        title="Onboarding completado"
        message="Tus preferencias se han configurado correctamente."
      />,
      { variant: 'success' }
    );
  };

  return (
    <MainWrapper className="mainwrapper">
      {showOnboarding && user?.role === 'investor' && (
        <OnboardingInvestorModal
          open={showOnboarding}
          onComplete={handleCompleteOnboarding}
          onClose={() => {
            sessionStorage.setItem('onboarding_dismissed', 'true');
            setOnboardingDismissed(true);
          }}
        />
      )}
      {showOnboarding && user?.role === 'psi' && (
        <OnboardingPsiModal open={showOnboarding} onComplete={handleCompleteOnboarding} />
      )}
      <Sidebar
        isSidebarOpen={true}
        isMobileSidebarOpen={isMobileSidebarOpen}
        onSidebarClose={() => setMobileSidebarOpen(false)}
      />
      <PageWrapper className="page-wrapper">
        <Header toggleMobileSidebar={() => setMobileSidebarOpen(true)} />
        <Container
          sx={{
            paddingTop: '10px',
            maxWidth: { xs: '100vw', md: '1200px' },
            width: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
        >
          {user && profileIncompleteBannerOpen && (
            <Collapse in={profileIncompleteBannerOpen} sx={{ mb: 2 }}>
              <Alert
                severity="warning"
                action={
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => navigate('/perfil?tab=contratacion')}
                  >
                    Completar datos
                  </Button>
                }
                sx={{ alignItems: 'center', fontSize: '1rem', fontWeight: 500 }}
              >
                Completa tus datos de contratación para generar y firmar contratos con inversores.
              </Alert>
            </Collapse>
          )}
          <Box sx={{ minHeight: 'calc(100vh - 170px)' }}>
            <Outlet />
          </Box>
        </Container>
      </PageWrapper>
      <WhatsAppFab />
    </MainWrapper>
  );
}
